import React, { useState, useEffect, useContext, useRef } from "react";

import MainPagination from "../common/MainPagination";
import MainTitle from "../common/MainTitle";
import TableMainSearch from "../common/MainSearch";
import AccessDeniedResponse from "../common/AccessDeniedResponse";

import ApplicantsList from "./ApplicantsList";
import ParityReport, { allReportPages } from '../common/ParityReport';
import { applicantColumns } from './ApplicantActions';
import { flashPrintBlocked, openPrintRoute } from '../prints/parity/AriaReport';
import '../common/parity.css';

import { YearContext } from "../../contexts/yearContext";
import { AuthContext } from "../../contexts/authContext";

import lbl from "../../data/Labels.json";
import db from "../../services/query";

/**
 * Read-only applicant list (PHP applicant_list parity, list view only).
 * Uses GET /students/applicants (ACL /students/applicants).
 */
const Applicants = () => {
  const { entryYear } = useContext(YearContext);
  const { accountProfile } = useContext(AuthContext);
  const scopedFacultyId =
    accountProfile?.faculty_id != null && Number(accountProfile.faculty_id) > 0
      ? Number(accountProfile.faculty_id)
      : null;

  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [facultyId, setFacultyId] = useState("");
  const [faculties, setFaculties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [printMsg, setPrintMsg] = useState("");
  const filtersRef = useRef({ search: "", facultyId: "" });
  const reportRoute = `students/applicants?${new URLSearchParams({entryYearId:String(entryYear || ''),facultyId:String(scopedFacultyId || facultyId || ''),search,orderBy:'student_id',order:'desc'})}`;

  useEffect(() => {
    if (scopedFacultyId) return;
    let cancelled = false;
    db.getAll("dropdowns/faculties")
      .then(({ data: body }) => {
        if (!cancelled) setFaculties(body?.data || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [scopedFacultyId]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(query), 350);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    filtersRef.current = { search, facultyId };
  }, [search, facultyId]);

  const requestData = async (p = {}) => {
    setLoading(true);
    setDenied(false);
    setErrorMsg("");
    try {
      const page = p.page || 1;
      const limit = p.limit || 25;
      const entryYearId = p.entryYearId ?? entryYear;
      const term = p.search !== undefined ? p.search : filtersRef.current.search;
      const fac =
        scopedFacultyId ||
        (p.facultyId !== undefined ? p.facultyId : filtersRef.current.facultyId);

      let route =
        `students/applicants?page=${page}&limit=${limit}` +
        `&orderBy=student_id&order=desc`;
      if (entryYearId) route += `&entryYearId=${entryYearId}`;
      if (term) route += `&search=${encodeURIComponent(term)}`;
      if (fac) route += `&facultyId=${fac}`;

      const { data: body } = await db.getAll(route);
      setData(body || { data: [] });
    } catch (err) {
      const status = err?.response?.status;
      const body = err?.response?.data;
      if (status === 403 || body?.error?.statusCode === 403) {
        setDenied(true);
        setData({ data: [] });
      } else {
        setData({ data: [] });
        setErrorMsg(body?.message || "بارگیری داوطلبان ناموفق بود.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    requestData({ page: 1, search, facultyId });
    // entryYear / search / facultyId drive reloads
  }, [entryYear, search, facultyId, scopedFacultyId]);

  return (
    <div className="main-content mis-surface parity-list applicants-page">
      <div className="main-content-header">
        <div className="right-content">
          <MainTitle title={lbl.Applicants} />
        </div>
        <div className="center-content">
          {!denied ? <TableMainSearch setQuery={setQuery} /> : null}
        </div>
        <div className="left-content">
          {!denied && <><a className="mis-button" href="https://admission.saawis.com" target="_blank" rel="noopener noreferrer">ثبت نام</a><ParityReport print={false} title="داوطلبان" columns={applicantColumns} loadRows={()=>allReportPages(reportRoute)} disabled={loading}/><button className="mis-button is-secondary" disabled={loading} onClick={()=>{if(!openPrintRoute(`/print/applicants?${new URLSearchParams({entryYearId:String(entryYear || ''),facultyId:String(scopedFacultyId || facultyId || ''),search})}`))flashPrintBlocked(setPrintMsg);}}>چاپ</button><button className="mis-button is-secondary" disabled={loading} onClick={()=>{if(!openPrintRoute(`/print/exam-cards?${new URLSearchParams({entryYearId:String(entryYear || ''),facultyId:String(scopedFacultyId || facultyId || ''),search})}`))flashPrintBlocked(setPrintMsg);}}>کارت امتحان کانکور</button></>}
          {printMsg ? <p role="status">{printMsg}</p> : null}
          {!denied && !scopedFacultyId && faculties.length > 0 ? (
            <div className="header-half-year">
              <main>
                <select
                  name="applicant-faculty"
                  value={facultyId}
                  onChange={(e) => setFacultyId(e.target.value)}
                  aria-label={lbl.Faculties}
                >
                  <option value="">{lbl.ApplicantsAllFaculties}</option>
                  {faculties.map((f) => (
                    <option key={f.id || f.faculty_id} value={f.id || f.faculty_id}>
                      {f.name || f.faculty_name}
                    </option>
                  ))}
                </select>
              </main>
            </div>
          ) : null}
        </div>
      </div>
      <div className="main-content-body pagination">
        {denied ? (
          <AccessDeniedResponse />
        ) : loading && !data ? (
          <p>{lbl.Loading}</p>
        ) : errorMsg ? (
          <p>{errorMsg}</p>
        ) : !data?.data?.length ? (
          <p>{lbl.ApplicantsEmpty}</p>
        ) : (
          <ApplicantsList data={data} query="" onSaved={()=>requestData({page:data?.page || 1})} />
        )}
      </div>
      <div className="main-content-footer">
        <div className="route-history-area" />
        <div className="pagination-area">
          {!denied && data?.totalPages ? (
            <MainPagination
              gotoPage={(p) =>
                requestData({
                  page: p.page,
                  entryYearId: entryYear,
                })
              }
              data={data}
              forceEntryYear={false}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Applicants;
